import { AlertCircle, Check, ChevronDown, Zap } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageToolCallDto } from '@/Domain/api/Conversation/dto/Message.dto';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { AnimatedCollapsibleContent } from '@/Domain/components/Shared/Collapsible/Collapsible';

type Props = {
  toolCalls: MessageToolCallDto[];
};

const RESULT_TRUNCATE_LENGTH = 120;

const ActionResultPreview = ({ preview }: { preview: string }) => {
  const { t } = useTranslation(['inbox_v2']);
  const [expanded, setExpanded] = useState(false);
  const isLong = preview.length > RESULT_TRUNCATE_LENGTH;

  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <div className="flex items-center gap-1">
        <span className="text-xs text-foreground-subtle italic shrink-0">
          {t('inbox_v2:ai_action_result')}:
        </span>
        {!expanded && (
          <span
            className="text-xs text-foreground-subtle italic truncate"
            data-testid="result-preview"
          >
            {isLong ? `${preview.slice(0, RESULT_TRUNCATE_LENGTH)}...` : preview}
          </span>
        )}
        {isLong && (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-blue-500 hover:text-blue-700 cursor-pointer shrink-0 transition-colors"
            data-testid="result-expand-toggle"
          >
            {expanded
              ? t('inbox_v2:ai_action_result_collapse')
              : t('inbox_v2:ai_action_result_expand')}
          </button>
        )}
      </div>
      {expanded && (
        <pre
          className="text-xs text-foreground-subtle bg-card/60 rounded p-2 overflow-x-auto max-h-48 whitespace-pre-wrap break-all border border-border"
          data-testid="result-full"
        >
          {preview}
        </pre>
      )}
    </div>
  );
};

const ActionCard = ({ toolCall }: { toolCall: MessageToolCallDto }) => {
  const { t } = useTranslation(['inbox_v2']);
  const isError = toolCall.status === 'error';

  return (
    <div
      className={`rounded-lg p-2.5 flex flex-col gap-1 min-w-0 ${
        isError ? 'bg-red-50 border border-red-100' : 'bg-muted border border-border'
      }`}
      data-testid="action-card"
    >
      <div className="flex items-center gap-1.5">
        {isError ? (
          <AlertCircle className="size-3 text-red-500 shrink-0" />
        ) : (
          <Zap className="size-3 text-foreground-subtle shrink-0" />
        )}
        <span
          className="text-xs font-medium text-muted-foreground truncate"
          data-testid="tool-name"
        >
          {toolCall.tool_name}
        </span>
        {toolCall.http_status_code != null && (
          <Badge
            variant={toolCall.http_status_code >= 400 ? 'error' : 'success'}
            size="xs"
            data-testid="http-status-badge"
          >
            {toolCall.http_status_code}
          </Badge>
        )}
        {toolCall.status === 'success' && <Check className="size-3 text-green-500 shrink-0" />}
        {toolCall.duration_ms != null && (
          <span
            className="text-xs text-foreground-subtle ml-auto shrink-0"
            data-testid="tool-duration"
          >
            {toolCall.duration_ms}ms
          </span>
        )}
      </div>

      {Object.keys(toolCall.tool_input).length > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5">
          {Object.entries(toolCall.tool_input).map(([key, value]) => (
            <span
              key={key}
              className="text-xs text-foreground-subtle"
              data-testid={`tool-input-${key}`}
            >
              {key}: <span className="font-mono">{String(value)}</span>
            </span>
          ))}
        </div>
      )}

      {isError && toolCall.tool_output_preview === ''
        ? null
        : toolCall.tool_output_preview && (
            <ActionResultPreview preview={toolCall.tool_output_preview} />
          )}

      {isError && toolCall.tool_output_preview === '' && (
        <span className="text-xs text-red-500" data-testid="tool-error">
          {t('inbox_v2:ai_action_error')}
        </span>
      )}
    </div>
  );
};

export const AIActionsCollapsible = ({ toolCalls }: Props) => {
  const { t } = useTranslation(['inbox_v2']);
  const [open, setOpen] = useState(false);

  if (!toolCalls || toolCalls.length === 0) return null;

  return (
    <div className="flex flex-col items-end overflow-hidden" data-testid="ai-actions-collapsible">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="text-xs text-foreground-subtle hover:text-muted-foreground cursor-pointer flex items-center gap-1.5 py-1 transition-colors"
        data-testid="ai-actions-toggle"
      >
        <Zap className="size-3" />
        <span>{t('inbox_v2:ai_actions_collapsed', { count: toolCalls.length })}</span>
        <ChevronDown className={`size-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatedCollapsibleContent open={open} className="w-full">
        <div className="flex flex-col gap-1.5 mt-1" data-testid="ai-actions-expanded">
          {toolCalls.map((toolCall, index) => (
            <ActionCard key={index} toolCall={toolCall} />
          ))}
        </div>
      </AnimatedCollapsibleContent>
    </div>
  );
};
